<div class="sync-monitoring">
	<!-- Toolbar: title (with last-updated + refresh beneath it) + environment selector -->
	<div class="sync-monitoring-toolbar">
		<div class="sync-monitoring-title-group">
			<h1 class="sync-monitoring-title">
				{{ 'administration.sync-monitoring.title' | translate }}
			</h1>
			<div class="sync-monitoring-status-line">
				@if (statusLastUpdatedAt) {
					<span class="sync-monitoring-last-updated">
						{{ 'administration.sync-monitoring.last-updated' | translate }}:
						{{ statusLastUpdatedDisplay }}
					</span>
				}
				@if (statusLastUpdatedAt && selectedEnvironmentId) {
					<span class="sync-monitoring-status-separator">•</span>
				}
				@if (selectedEnvironmentId) {
					<mat-slide-toggle class="sync-monitoring-auto-refresh-toggle" color="primary" labelPosition="before" [checked]="!isAutoRefreshPaused" (change)="onToggleAutoRefresh($event.checked)">
						<span class="sync-monitoring-next-refresh">
							{{ (isAutoRefreshPaused ? 'administration.sync-monitoring.auto-refresh-paused' : 'administration.sync-monitoring.next-refresh') | translate: { seconds: nextRefreshInSeconds } }}
						</span>
					</mat-slide-toggle>
				}
				<button mat-icon-button class="sync-monitoring-refresh" [disabled]="isTableLoading || !selectedEnvironmentId" (click)="refresh()" [attr.aria-label]="'administration.sync-monitoring.refresh' | translate">
					<mat-icon svgIcon="acies_renew"></mat-icon>
				</button>
			</div>
		</div>

		@if (environmentOptions.length > 1) {
			<mat-form-field appearance="outline" class="sync-monitoring-env-select">
				<mat-label>
					{{ 'administration.sync-monitoring.environment-selector' | translate }}
				</mat-label>
				<mat-select [value]="selectedEnvironmentId" [disabled]="selectorDisabled" (selectionChange)="onEnvironmentChange($event.value)">
					@for (option of environmentOptions; track option) {
						<mat-option [value]="option.id">{{ option.name }}</mat-option>
					}
				</mat-select>
			</mat-form-field>
		}
	</div>

	<!-- Initial load -->
	@if (isLoading) {
		<div class="sync-monitoring-loading">
			<mat-progress-spinner diameter="40" mode="indeterminate"></mat-progress-spinner>
		</div>
	}

	<ng-container *ngIf="!isLoading">
		<!-- Overview panel (R4) -->
		<mat-card class="sync-monitoring-overview" *ngIf="selectedEnvironment as env">
			<div class="sync-monitoring-overview-header">
				<span class="sync-monitoring-env-name">{{ env.name }}</span>
				<span class="sync-monitoring-env-version" *ngIf="env.version">v{{ env.version }}</span>
				<span class="sync-monitoring-status-separator" *ngIf="env.version && selectedEnvironmentId">•</span>
				<span class="sync-monitoring-env-id" *ngIf="selectedEnvironmentId">
					{{ 'administration.sync-monitoring.overview.id' | translate }}:
					{{ selectedEnvironmentId }}
				</span>
				<mat-slide-toggle *ngIf="hasSuperadminPermission" class="sync-monitoring-sync-toggle" color="primary" [checked]="!isSyncDisabled" [disabled]="isTogglingSync" (change)="onToggleSynchronization($event.checked)">
					{{ (isSyncDisabled ? 'administration.sync-monitoring.workers.sync-disabled' : 'administration.sync-monitoring.workers.sync-enabled') | translate }}
				</mat-slide-toggle>
				<span class="sm-badge" *ngIf="!hasSuperadminPermission" [ngClass]="isSyncDisabled ? 'sm-badge-warn' : 'sm-badge-ok'">
					{{ (isSyncDisabled ? 'administration.sync-monitoring.workers.sync-disabled' : 'administration.sync-monitoring.workers.sync-enabled') | translate }}
				</span>
			</div>

			<div class="sync-monitoring-flags">
				<span class="sync-monitoring-flag">
					<mat-icon [svgIcon]="env.ready ? 'acies_true' : 'acies_false'" [ngClass]="env.ready ? 'greenColor' : 'redColor'"></mat-icon>
					{{ 'administration.sync-monitoring.overview.ready' | translate }}
				</span>
				<span class="sync-monitoring-flag">
					<mat-icon [svgIcon]="env.synchronized ? 'acies_true' : 'acies_false'" [ngClass]="env.synchronized ? 'greenColor' : 'redColor'"></mat-icon>
					{{ 'administration.sync-monitoring.overview.synchronized' | translate }}
				</span>
				<!-- Stable: every core-engine table is synchronized too -->
				<span class="sync-monitoring-flag">
					<mat-icon [svgIcon]="env.stable ? 'acies_true' : 'acies_false'" [ngClass]="env.stable ? 'greenColor' : 'redColor'"></mat-icon>
					{{ 'administration.sync-monitoring.overview.engine-synchronized' | translate }}
				</span>
			</div>

			<div class="sync-monitoring-overview-row" *ngIf="env.lifecycleInfo as lifecycleInfo">
				<span class="sync-monitoring-label">{{ 'administration.sync-monitoring.overview.lifecycle-state' | translate }}:</span>
				<span>{{ lifecycleInfo.state || '—' }} (gen {{ lifecycleInfo.generation }})</span>
			</div>
			<div class="sync-monitoring-overview-row sync-monitoring-error" *ngIf="env.lifecycleInfo?.error">
				{{ env.lifecycleInfo?.error }}
			</div>

			<!-- A repair core-one found for this generation and left to an operator. The recommended
			     action is the primary button; the other one is the fallback. -->
			<div class="sync-monitoring-repair-needed" *ngIf="env.repairNeeded as repair" role="alert">
				<div class="sync-monitoring-repair-needed-title">
					<mat-icon svgIcon="acies_false" class="redColor"></mat-icon>
					<strong>{{ 'administration.sync-monitoring.repair-needed.title' | translate }}</strong>
					<span *ngIf="repair.category">({{ repair.category }})</span>
				</div>
				<div class="sync-monitoring-overview-row" *ngIf="repair.reason">
					<span class="sync-monitoring-label">{{ 'administration.sync-monitoring.repair-needed.reason' | translate }}:</span>
					<span>{{ repair.reason }}</span>
				</div>
				<div class="sync-monitoring-overview-row" *ngIf="repair.tables?.length">
					<span class="sync-monitoring-label">{{ 'administration.sync-monitoring.repair-needed.tables' | translate }}:</span>
					<span>{{ repair.tables?.join(', ') }}</span>
				</div>
				<div class="sync-monitoring-overview-row" *ngIf="repair.detectedAt">
					<span class="sync-monitoring-label">{{ 'administration.sync-monitoring.repair-needed.detected' | translate }}:</span>
					<span>{{ repair.detectedAt | date: 'short' }}</span>
				</div>
				<div class="sync-monitoring-repair-needed-reason" *ngIf="repair.recommendedActionReason">{{ repair.recommendedActionReason }}</div>
				<div class="sync-monitoring-repair-needed-actions" *ngIf="hasSuperadminPermission">
					<ng-container *ngIf="isRepairRecommended; else resetTablesRecommended">
						<button mat-raised-button color="primary" (click)="openRepairDialog()">
							{{ 'administration.sync-monitoring.repair-needed.repair-recommended' | translate }}
						</button>
						<button mat-stroked-button [disabled]="isTableLoading" (click)="openRepairNeededResetDialog()">
							{{ 'administration.sync-monitoring.repair-needed.reset-tables' | translate }}
						</button>
					</ng-container>
					<ng-template #resetTablesRecommended>
						<button mat-raised-button color="primary" [disabled]="isTableLoading" (click)="openRepairNeededResetDialog()">
							{{ 'administration.sync-monitoring.repair-needed.reset-tables-recommended' | translate }}
						</button>
						<button mat-stroked-button (click)="openRepairDialog()">
							{{ 'administration.sync-monitoring.repair-needed.repair' | translate }}
						</button>
					</ng-template>
				</div>
			</div>

			<div class="sync-monitoring-overview-row" *ngIf="env.synchronization?.progress as progress">
				<span class="sync-monitoring-label">{{ 'administration.sync-monitoring.overview.progress' | translate }}:</span>
				<span>{{ progress.reason || '—' }} ({{ progress.estimatedPendingTableCount }} {{ 'administration.sync-monitoring.overview.pending-tables' | translate }})</span>
			</div>

			<!-- Drift audit: the environment's latest audit state, compared item by item with core-engine -->
			<div class="sm-audit-box" *ngIf="envAudit as audit">
				<div class="sm-audit-box-main">
					<h2 class="sync-monitoring-subtitle">{{ 'administration.sync-monitoring.audit.title' | translate }}</h2>
					<div class="sm-audit-state">
						<!-- Aligned: one green label; otherwise the status and how many tables differ. -->
						<ng-container *ngIf="audit.status === 'aligned'; else auditNotAligned">
							<span class="sm-badge sm-badge-ok">{{ 'administration.sync-monitoring.audit.aligned-label' | translate }}</span>
						</ng-container>
						<ng-template #auditNotAligned>
							<span class="sm-badge" [ngClass]="audit.status === 'differing' ? 'sm-badge-warn' : ''">
								{{ 'administration.sync-monitoring.audit.statuses.' + (audit.status ?? 'never_audited') | translate }}
							</span>
							<span class="sm-muted">{{ 'administration.sync-monitoring.audit.differing-tables' | translate: { count: differingTableCount, total: auditableTables.length } }}</span>
						</ng-template>
						<span class="sm-badge sm-badge-exhausted" *ngIf="audit.hasExhaustedRepairs">{{ 'administration.sync-monitoring.audit.exhausted-badge' | translate }}</span>
					</div>
					<dl class="sm-audit-kv">
						<dt>{{ 'administration.sync-monitoring.audit.latest-audit' | translate }}</dt>
						<dd>{{ formatEpoch(latestAuditAt) }}</dd>
						<dt>{{ 'administration.sync-monitoring.audit.last-full-pass' | translate }}</dt>
						<dd>{{ formatEpoch(audit.lastFullPassAt ?? null) }}</dd>
					</dl>
					<!-- Next run, full pass schedule and repair settings, folded away by default -->
					<details class="sm-audit-details">
						<summary>{{ 'administration.sync-monitoring.audit.details' | translate }}</summary>
						<dl class="sm-audit-kv">
							<dt>{{ 'administration.sync-monitoring.audit.next-run' | translate }}</dt>
							<dd>
								<ng-container *ngIf="audit.nextRun?.at; else noNextRun">
									{{ formatEpoch(audit.nextRun?.at ?? null) }} ·
									{{ (audit.nextRun?.fullPass ? 'administration.sync-monitoring.audit.full-pass' : 'administration.sync-monitoring.audit.recheck') | translate }}
								</ng-container>
								<ng-template #noNextRun>—</ng-template>
							</dd>
							<dt>{{ 'administration.sync-monitoring.audit.full-pass-day' | translate }}</dt>
							<dd>{{ 'administration.sync-monitoring.audit.full-pass-every' | translate: { weekday: audit.fullPassWeekday ? ('administration.sync-monitoring.audit.weekdays.' + audit.fullPassWeekday | translate) : '—', days: audit.settings?.fullPassDays ?? '—' } }}</dd>
							<dt>{{ 'administration.sync-monitoring.audit.repair' | translate }}</dt>
							<dd>
								{{
									'administration.sync-monitoring.audit.settings'
										| translate
											: {
													replay: ((audit.settings?.replay?.enabled ? 'administration.sync-monitoring.audit.on' : 'administration.sync-monitoring.audit.off') | translate),
													cap: audit.settings?.replay?.capPerTable ?? '—',
													removal: audit.settings?.removal?.mode ? ('administration.sync-monitoring.audit.modes.' + audit.settings?.removal?.mode | translate) : '—',
													sweep: audit.settings?.sweep?.mode ? ('administration.sync-monitoring.audit.modes.' + audit.settings?.sweep?.mode | translate) : '—',
													window: audit.settings?.sweep?.windowDays ?? '—',
											  }
								}}
							</dd>
						</dl>
					</details>
					<div class="sm-audit-exhausted" *ngIf="exhaustedTables.length > 0" role="alert">
						<strong>{{ 'administration.sync-monitoring.audit.exhausted-title' | translate: { count: auditTotals?.exhausted ?? 0 } }}</strong>
						<span>
							<ng-container *ngFor="let table of exhaustedTables; let last = last">{{ table.tableName }} ({{ table.count }}){{ last ? '' : ', ' }}</ng-container>
						</span>
						<span>{{ 'administration.sync-monitoring.audit.exhausted-text' | translate }}</span>
					</div>
					<div class="sm-audit-buttons">
						<button mat-raised-button color="primary" *ngIf="canManageAudit" [disabled]="!selectedEnvironmentId || isTableLoading" (click)="openDriftAuditDialog()">
							{{ 'administration.sync-monitoring.audit.toolbar' | translate }}
						</button>
						<button mat-stroked-button class="sm-icon-button" [disabled]="!selectedEnvironmentId" (click)="openReportDialog()">
							{{ 'administration.sync-monitoring.audit.report' | translate }}
							<mat-icon svgIcon="acies_download"></mat-icon>
						</button>
					</div>
				</div>
				<div class="sm-audit-box-counts" *ngIf="auditTotals as totals">
					<h4>{{ 'administration.sync-monitoring.audit.findings-title' | translate }}</h4>
					<div class="sm-audit-counts">
						<div [class.sm-hot]="totals.missing > 0">
							<b>{{ totals.missing }}</b>
							<span>{{ 'administration.sync-monitoring.audit.kinds.auditDriftMissingInEngine' | translate }}</span>
						</div>
						<div [class.sm-hot]="totals.mismatch > 0">
							<b>{{ totals.mismatch }}</b>
							<span>{{ 'administration.sync-monitoring.audit.kinds.auditDriftRowVersionMismatch' | translate }}</span>
						</div>
						<div>
							<b>{{ totals.extra }}</b>
							<span>{{ 'administration.sync-monitoring.audit.kinds.auditDriftExtraInEngine' | translate }}</span>
						</div>
						<div>
							<b>{{ totals.offVersion }}</b>
							<span>{{ 'administration.sync-monitoring.audit.kinds.auditDriftOffVersion' | translate }}</span>
						</div>
					</div>
					<h4>{{ 'administration.sync-monitoring.audit.repair-title' | translate }}</h4>
					<div class="sm-audit-counts">
						<div>
							<b>{{ totals.replayed }}</b>
							<span>{{ 'administration.sync-monitoring.audit.outcomes.issued' | translate }}</span>
						</div>
						<div [class.sm-hot]="totals.failed > 0">
							<b>{{ totals.failed }}</b>
							<span>{{ 'administration.sync-monitoring.audit.outcomes.payloadUnavailable' | translate }}</span>
						</div>
						<div [class.sm-hot]="totals.deferred > 0">
							<b>{{ totals.deferred }}</b>
							<span>{{ 'administration.sync-monitoring.audit.outcomes.deferred' | translate }}</span>
						</div>
						<div [class.sm-hot]="totals.exhausted > 0">
							<b>{{ totals.exhausted }}</b>
							<span>{{ 'administration.sync-monitoring.audit.outcomes.exhausted' | translate }}</span>
						</div>
					</div>
				</div>
			</div>
		</mat-card>

		<!-- Workers section (R10) — placed above the view tabs so it stays visible regardless of table size -->
		<div class="sync-monitoring-section sync-monitoring-workers">
			<div class="sync-monitoring-workers-header">
				<h2 class="sync-monitoring-subtitle">
					{{ 'administration.sync-monitoring.workers.title' | translate }}
				</h2>
				<button mat-raised-button color="primary" class="sync-monitoring-create-worker sm-icon-button" *ngIf="hasSuperadminPermission" [disabled]="!selectedEnvironmentId || isTableLoading" (click)="openCreateWorkerDialog()">
					{{ 'administration.sync-monitoring.workers.create' | translate }}
					<mat-icon svgIcon="acies_add"></mat-icon>
				</button>
			</div>
			<div class="sync-monitoring-error" *ngIf="workersError">
				{{ workersError | translate }}
			</div>
			<ng-container *ngIf="!workersError">
				<div class="sync-monitoring-empty" *ngIf="workers.length === 0">
					{{ 'administration.sync-monitoring.workers.empty' | translate }}
				</div>
				<div class="sm-worker-card" *ngFor="let worker of workers" [class.sm-worker-stale]="worker.isStale">
					<div class="sm-worker-header">
						<strong>{{ worker.name }}</strong>
						<span class="sm-worker-id" *ngIf="worker.id">
							{{ 'administration.sync-monitoring.workers.id' | translate }}:
							{{ worker.id }}
						</span>
						<span class="sm-badge sm-badge-ok" *ngIf="worker.hasHeartbeat && !worker.isStale" [matTooltip]="'administration.sync-monitoring.workers.active-hint' | translate">
							{{ 'administration.sync-monitoring.workers.active' | translate }}
						</span>
						<span class="sm-badge sm-badge-warn" *ngIf="worker.isStale" [matTooltip]="'administration.sync-monitoring.workers.inactive-hint' | translate">
							{{ 'administration.sync-monitoring.workers.inactive' | translate }}
						</span>
						<span class="sm-badge sm-badge-warn" *ngIf="worker.heartbeatUnavailable">
							{{ 'administration.sync-monitoring.workers.heartbeat-unavailable' | translate }}
						</span>
						<span class="sm-badge" *ngIf="!worker.hasHeartbeat && !worker.heartbeatUnavailable">
							{{ 'administration.sync-monitoring.workers.never-reported' | translate }}
						</span>
						<span class="sm-worker-actions" *ngIf="hasSuperadminPermission">
							<button mat-icon-button type="button" [disabled]="isTableLoading" (click)="openEditWorkerDialog(worker)" [attr.aria-label]="'administration.sync-monitoring.workers.edit' | translate" [matTooltip]="'administration.sync-monitoring.workers.edit' | translate">
								<mat-icon svgIcon="acies_edit"></mat-icon>
							</button>
							<button mat-icon-button type="button" [disabled]="isTableLoading" (click)="confirmDeleteWorker(worker)" [attr.aria-label]="'administration.sync-monitoring.workers.delete' | translate" [matTooltip]="'administration.sync-monitoring.workers.delete' | translate">
								<mat-icon svgIcon="acies_delete"></mat-icon>
							</button>
						</span>
					</div>
					<div class="sm-worker-filters" *ngIf="worker.filters.length > 0">
						<span class="sm-worker-filters-label">{{ 'administration.sync-monitoring.workers.filters' | translate }}:</span>
						<span class="sm-badge" *ngFor="let filter of worker.filters">{{ filter.name }}: {{ filter.value }}</span>
					</div>
					<div class="sm-worker-meta" *ngIf="worker.hasHeartbeat">
						<span>{{ 'administration.sync-monitoring.workers.last-heartbeat' | translate }}: {{ worker.lastHeartbeatFormatted }}</span>
						<span>{{ 'administration.sync-monitoring.workers.agent-version' | translate }}: {{ worker.jobAgentVersion ?? '—' }}</span>
						<span>
							{{ 'administration.sync-monitoring.workers.db-version' | translate }}:
							{{ worker.databaseVersion ?? '—' }}
						</span>
						<span>
							{{ 'administration.sync-monitoring.workers.ip' | translate }}:
							{{ worker.ipAddress ?? '—' }}
						</span>
						<span>
							{{ 'administration.sync-monitoring.workers.host' | translate }}:
							{{ worker.hostName ?? '—' }}
						</span>
						<span>
							{{ 'administration.sync-monitoring.workers.db-server' | translate }}:
							{{ worker.dbServerName ?? '—' }}
						</span>
						<span>
							{{ 'administration.sync-monitoring.workers.db-name' | translate }}:
							{{ worker.dbName ?? '—' }}
						</span>
					</div>
					<details class="sm-worker-config" *ngIf="worker.hasHeartbeat" (toggle)="loadHeartbeatHistory(worker.id)">
						<summary>
							{{ 'administration.sync-monitoring.workers.heartbeats' | translate }}
							<ng-container *ngIf="heartbeatRows(worker) as rows">
								<span class="sm-badge sm-badge-warn" *ngIf="heartbeatRestarts(rows) > 0">{{ 'administration.sync-monitoring.workers.restarts' | translate: { count: heartbeatRestarts(rows) } }}</span>
								<span class="sm-badge sm-badge-ok" *ngIf="heartbeatRestarts(rows) === 0 && rows.length > 0">{{ 'administration.sync-monitoring.workers.same-process' | translate: { pid: rows[0].pid ?? '—' } }}</span>
							</ng-container>
						</summary>
						<div class="sync-monitoring-error" *ngIf="heartbeatFailed(worker)">{{ 'administration.sync-monitoring.errors.heartbeats-load-failed' | translate }}</div>
						<table class="sm-heartbeats" *ngIf="heartbeatRows(worker) as rows">
							<thead>
								<tr>
									<th>{{ 'administration.sync-monitoring.workers.last-heartbeat' | translate }}</th>
									<th>PID</th>
									<th>{{ 'administration.sync-monitoring.workers.uptime' | translate }}</th>
									<th>{{ 'administration.sync-monitoring.workers.host' | translate }}</th>
									<th>{{ 'administration.sync-monitoring.workers.agent-version' | translate }}</th>
								</tr>
							</thead>
							<tbody>
								<tr *ngFor="let row of rows" [class.sm-heartbeat-restart]="row.restarted">
									<td>{{ row.at }}</td>
									<td>{{ row.pid ?? '—' }}</td>
									<td>
										{{ row.uptime }}
										<span class="sm-badge sm-badge-warn" *ngIf="row.restarted">{{ 'administration.sync-monitoring.workers.restarted' | translate }}</span>
									</td>
									<td>{{ row.hostName ?? '—' }}</td>
									<td>{{ row.jobAgentVersion ?? '—' }}</td>
								</tr>
							</tbody>
						</table>
					</details>
					<details class="sm-worker-config" *ngIf="worker.oneConfiguration">
						<summary>
							{{ 'administration.sync-monitoring.workers.configuration' | translate }}
						</summary>
						<pre>{{ formatConfiguration(worker.oneConfiguration) }}</pre>
					</details>
				</div>
			</ng-container>
		</div>

		<mat-card class="sync-monitoring-status-panel">
			<div class="sync-monitoring-status-panel-header">
				<h2 class="sync-monitoring-subtitle">
					{{ 'administration.sync-monitoring.views.title' | translate }}
				</h2>
				<button mat-raised-button color="primary" class="sync-monitoring-reset-all" *ngIf="hasSuperadminPermission" [disabled]="!selectedEnvironmentId || isTableLoading" (click)="openResetDialog(null)">
					{{ 'administration.sync-monitoring.reset.toolbar' | translate }}
				</button>
			</div>

			<!-- View switch (Material tab bar, styled like scim-administration) -->
			<nav mat-tab-nav-bar [tabPanel]="viewTabPanel" class="sync-monitoring-views">
				<a mat-tab-link [active]="activeView === 'tables'" (click)="setActiveView('tables')">
					{{ 'administration.sync-monitoring.views.tables' | translate }}
				</a>
				<a mat-tab-link [active]="activeView === 'audit'" (click)="setActiveView('audit')">
					{{ 'administration.sync-monitoring.views.audit' | translate }}
					<span class="sm-badge sm-badge-warn sm-tab-badge" *ngIf="differingTableCount > 0">{{ differingTableCount }}</span>
				</a>
				<a mat-tab-link [active]="activeView === 'errors'" (click)="setActiveView('errors')">
					{{ 'administration.sync-monitoring.views.errors' | translate }}
				</a>
				<a mat-tab-link [active]="activeView === 'transitions'" (click)="setActiveView('transitions')">
					{{ 'administration.sync-monitoring.views.transitions' | translate }}
				</a>
			</nav>

			<mat-tab-nav-panel #viewTabPanel>
				<!-- Tables view (R5-R8, R16) -->
				<div class="sync-monitoring-section sync-monitoring-section--status" *ngIf="activeView === 'tables'">
					<div class="sync-monitoring-loading" *ngIf="isTableLoading">
						<mat-progress-spinner diameter="28" mode="indeterminate"></mat-progress-spinner>
					</div>

					<div class="sync-monitoring-empty" *ngIf="!isTableLoading && tableSource.data.length === 0">
						{{ 'administration.sync-monitoring.tables.empty' | translate }}
					</div>

					<ng-container *ngIf="tableSource.data.length > 0">
						<div class="sm-strip sm-strip-bad" role="status" *ngIf="differingTableCount > 0">
							<span class="sm-badge sm-badge-warn">{{ 'administration.sync-monitoring.tables.differ-strip' | translate: { count: differingTableCount } }}</span>
							<span>{{ 'administration.sync-monitoring.tables.differ-strip-text' | translate: auditTotals ?? {} }}</span>
							<button mat-stroked-button type="button" (click)="setActiveView('audit')">{{ 'administration.sync-monitoring.tables.show-in-audit' | translate }}</button>
						</div>
						<mat-chip-listbox class="sm-filter-chips" [value]="tableFilter" (change)="setTableFilter($event.value ?? 'all')" [attr.aria-label]="'administration.sync-monitoring.tables.filter' | translate">
							<mat-chip-option value="all">{{ 'administration.sync-monitoring.tables.filters.all' | translate }} ({{ tableSource.data.length }})</mat-chip-option>
							<mat-chip-option value="audit">{{ 'administration.sync-monitoring.tables.filters.audit' | translate }} ({{ auditIssueCount }})</mat-chip-option>
							<mat-chip-option value="in-engine">{{ 'administration.sync-monitoring.tables.filters.in-engine' | translate }} ({{ inEngineCount }})</mat-chip-option>
							<mat-chip-option value="engine">{{ 'administration.sync-monitoring.tables.filters.engine' | translate }} ({{ notInEngineCount }})</mat-chip-option>
						</mat-chip-listbox>
					</ng-container>

					<app-generictable
						*ngIf="tableSource.data.length > 0"
						[tableSource]="tableSource"
						[tableColumns]="tableColumns"
						[rowActions]="rowActions"
						[rowClass]="rowClassFn"
						[expandableDetailTemplate]="tableDetailTpl"
						[expandedRow]="expandedRow"
						[showPaginator]="false"
						(rowExpandToggle)="onRowExpandToggle($event)"
						(menuEmitter)="handleRowMenuAction($event)"></app-generictable>

					<ng-template #tableDetailTpl let-row>
						<div class="sm-detail" [class.sm-detail-stale]="row.stale">
							<!-- Item sync progress (R6) -->
							<div class="sm-detail-block" *ngIf="row.queuedCount !== null || row.processedCount !== null || row.stage || row.reason">
								<h4>
									{{ 'administration.sync-monitoring.detail.item-sync' | translate }}
								</h4>
								<div>
									{{ 'administration.sync-monitoring.detail.processed' | translate }}: {{ row.processedCount ?? 0 }}
									{{ 'administration.sync-monitoring.detail.of' | translate }}
									{{ row.queuedCount ?? 0 }}
								</div>
								<div *ngIf="row.stage">
									{{ 'administration.sync-monitoring.detail.stage' | translate }}:
									{{ row.stage }}
								</div>
								<div *ngIf="row.reason">{{ 'administration.sync-monitoring.detail.reason' | translate }}: {{ row.reason }}</div>
								<div class="sm-detail-badges">
									<span class="sm-badge sm-badge-ok" *ngIf="row.sourceSyncComplete === true">
										{{ 'administration.sync-monitoring.detail.source-complete' | translate }}
									</span>
									<span class="sm-badge sm-badge-warn" *ngIf="row.sourceSyncComplete === false">
										{{ 'administration.sync-monitoring.detail.source-pending' | translate }}
									</span>
									<span class="sm-badge sm-badge-ok" *ngIf="row.coreProcessingComplete === true">
										{{ 'administration.sync-monitoring.detail.core-complete' | translate }}
									</span>
									<span class="sm-badge sm-badge-warn" *ngIf="row.coreProcessingComplete === false">
										{{ 'administration.sync-monitoring.detail.core-pending' | translate }}
									</span>
									<span class="sm-badge sm-badge-warn" *ngIf="row.stale">
										{{ 'administration.sync-monitoring.detail.stale' | translate }}
										{{ row.staleDuration }}
									</span>
								</div>
							</div>

							<!-- Downstream targets (R7) -->
							<div class="sm-detail-block" *ngIf="row.targets && row.targets.length > 0">
								<h4>
									{{ 'administration.sync-monitoring.detail.targets' | translate }}
								</h4>
								<div class="sm-target" *ngFor="let target of row.targets">
									<mat-icon [svgIcon]="isTargetSynchronized(target) ? 'acies_true' : 'acies_false'" [ngClass]="isTargetSynchronized(target) ? 'greenColor' : 'redColor'"></mat-icon>
									<span>{{ target.name }}</span>
									<span class="sm-target-counts" *ngIf="hasTargetRowSnapshot(target)">
										{{ 'administration.sync-monitoring.detail.rows' | translate }}: {{ target.targetObservedRowCount }}, {{ 'administration.sync-monitoring.detail.max-row-version' | translate }}:
										{{ target.targetObservedMaxRowVersion ?? '—' }}
									</span>
									<span class="sm-target-counts" *ngIf="!hasTargetRowSnapshot(target)">
										{{ 'administration.sync-monitoring.detail.queued' | translate }}: {{ target.queuedItemCount ?? 0 }}, {{ 'administration.sync-monitoring.detail.completed' | translate }}: {{ target.completedItemCount ?? 0 }}, {{ 'administration.sync-monitoring.detail.pending' | translate }}:
										{{ target.pendingItemCount ?? 0 }}
									</span>
								</div>
							</div>

							<!-- Synchronization metadata (R8) -->
							<div class="sm-detail-block">
								<h4>
									{{ 'administration.sync-monitoring.detail.synchronization' | translate }}
								</h4>
								<div>{{ 'administration.sync-monitoring.detail.ignore' | translate }}: {{ row.ignore }}</div>
								<div>{{ 'administration.sync-monitoring.detail.requisition-id' | translate }}: {{ row.requisitionId }}</div>
								<div>{{ 'administration.sync-monitoring.detail.row-version' | translate }}: {{ row.syncRowVersion ?? '—' }}</div>
								<div>{{ 'administration.sync-monitoring.detail.last-request' | translate }}: {{ formatEpoch(row.lastRequestAt) }}</div>
								<div>{{ 'administration.sync-monitoring.detail.last-callback' | translate }}: {{ formatEpoch(row.lastCallbackAt) }}</div>
								<div [class.sm-delay-high]="isDelayHigh(row.delay)">
									{{ 'administration.sync-monitoring.detail.delay' | translate }}:
									{{ row.delayDisplay ?? '—' }}
								</div>
								<div class="sync-monitoring-error" *ngIf="row.syncError" [matTooltip]="row.syncError">
									{{ 'administration.sync-monitoring.detail.error' | translate }}:
									{{ truncateSyncError(row.syncError) }}
								</div>
							</div>

							<!-- Source drift between on-prem and Core One -->
							<div class="sm-detail-block">
								<h4>{{ 'administration.sync-monitoring.source-drift.title' | translate }}</h4>
								<div *ngIf="!row.sourceDrift">{{ 'administration.sync-monitoring.source-drift.none' | translate }}</div>
								<ng-container *ngIf="row.sourceDrift as drift">
									<div>
										{{ 'administration.sync-monitoring.source-drift.counts' | translate: { source: drift.sourceCount ?? '—', coreOne: drift.coreOneCount ?? '—', difference: drift.difference } }}
									</div>
									<div>{{ 'administration.sync-monitoring.source-drift.first-seen' | translate }}: {{ formatEpoch(drift.firstSeenAt) }}</div>
									<div>{{ 'administration.sync-monitoring.source-drift.last-checked' | translate }}: {{ formatEpoch(drift.lastCheckedAt) }}</div>
									<div>{{ 'administration.sync-monitoring.source-drift.consecutive-audits' | translate }}: {{ drift.consecutiveAudits ?? '—' }}</div>
									<div *ngIf="drift.checkUnavailable">{{ 'administration.sync-monitoring.source-drift.unavailable' | translate }}</div>
									<ng-container *ngIf="!drift.checkUnavailable">
										<div *ngIf="drift.exhausted === true">{{ 'administration.sync-monitoring.source-drift.exhausted' | translate }}</div>
										<ng-container *ngIf="drift.lastRun as run">
											<h4>{{ 'administration.sync-monitoring.source-drift.last-run' | translate }}</h4>
											<div>
												<span class="sm-badge" [ngClass]="run.state === 'failed' ? 'sm-badge-warn' : run.state === 'completed' ? 'sm-badge-ok' : ''">
													{{ sourceDriftKey('states', run.state) | translate: { value: run.state } }}
												</span>
											</div>
											<div *ngIf="run.reason">{{ sourceDriftKey('reasons', run.reason) | translate: { value: run.reason } }}</div>
											<div>{{ 'administration.sync-monitoring.source-drift.started' | translate }}: {{ formatEpoch(run.startedAt) }}</div>
											<div>{{ 'administration.sync-monitoring.source-drift.finished' | translate }}: {{ formatEpoch(run.finishedAt) }}</div>
											<div>{{ 'administration.sync-monitoring.source-drift.ranges-checked' | translate }}: {{ run.rangesChecked ?? '—' }}</div>
											<div>{{ 'administration.sync-monitoring.source-drift.leaf-ranges' | translate }}: {{ run.leafRanges ?? '—' }}</div>
											<div>{{ 'administration.sync-monitoring.source-drift.missing-found' | translate }}: {{ run.missingFound ?? '—' }}</div>
											<div>{{ 'administration.sync-monitoring.source-drift.resent' | translate }}: {{ run.resent ?? '—' }}</div>
											<div>{{ 'administration.sync-monitoring.source-drift.extra-found' | translate }}: {{ run.extraFound ?? '—' }}</div>
											<div>{{ 'administration.sync-monitoring.source-drift.expired' | translate }}: {{ run.expired ?? '—' }}</div>
											<div *ngIf="isFoundNotRepaired(run)">{{ 'administration.sync-monitoring.source-drift.found-not-repaired' | translate }}</div>
										</ng-container>
									</ng-container>
								</ng-container>
								<button mat-stroked-button type="button" (click)="openSourceDriftDialog(row.tableName)">{{ 'administration.sync-monitoring.source-drift.range-check' | translate }}</button>
							</div>

							<!-- Per-table errors (R16) -->
							<div class="sm-detail-block">
								<h4>
									{{ 'administration.sync-monitoring.detail.errors' | translate }}
								</h4>
								<mat-progress-spinner *ngIf="expandedTableErrorsLoading" diameter="20" mode="indeterminate"></mat-progress-spinner>
								<div class="sync-monitoring-error" *ngIf="expandedTableErrorsError">
									{{ expandedTableErrorsError | translate }}
								</div>
								<ng-container *ngIf="!expandedTableErrorsLoading && !expandedTableErrorsError">
									<div *ngIf="expandedTableErrors.length === 0">
										{{ 'administration.sync-monitoring.detail.no-errors' | translate }}
									</div>
									<div class="sm-table-error" *ngFor="let err of expandedTableErrors">
										<span class="sm-badge" [ngClass]="err.isRetriable ? 'sm-badge-ok' : 'sm-badge-warn'">
											{{ (err.isRetriable ? 'administration.sync-monitoring.errors.retriable' : 'administration.sync-monitoring.errors.non-retriable') | translate }}
										</span>
										{{ err.failure }}
										<span class="sm-muted">
											({{ err.failureKind }}
											<ng-container *ngIf="err.failureScope">, {{ err.failureScope }}</ng-container>
											<ng-container *ngIf="err.failureStage">, {{ err.failureStage }}</ng-container>
											)
										</span>
									</div>
								</ng-container>
							</div>
						</div>
					</ng-template>
				</div>

				<!-- Audit view: each table's last drift audit, its findings and the repair actions -->
				<app-audit-tab
					*ngIf="activeView === 'audit'"
					[environmentId]="selectedEnvironmentId"
					[environmentName]="selectedEnvironment?.name ?? ''"
					[canManage]="canManageAudit"
					[canResetSync]="hasSuperadminPermission"
					[status]="status"
					[focusTable]="auditFocus"
					(changed)="refresh()"
					(auditRequested)="openDriftAuditDialog($event)"
					(resetRequested)="openResetDialogForTable($event)"></app-audit-tab>

				<!-- Errors view (R9) -->
				<div class="sync-monitoring-section sync-monitoring-section--status" *ngIf="activeView === 'errors'">
					<div class="sync-monitoring-error" *ngIf="errorsError">
						{{ errorsError | translate }}
					</div>
					<ng-container *ngIf="!errorsError">
						<div class="sync-monitoring-empty" *ngIf="errors.length === 0">
							{{ 'administration.sync-monitoring.errors.empty' | translate }}
						</div>
						<div class="sm-error-row" *ngFor="let err of errors" [ngClass]="err.isRetriable ? 'sm-error-retriable' : 'sm-error-nonretriable'" (click)="navigateToTable(err.tableSelf, err.failureKind)">
							<span class="sm-transition-time" *ngIf="err.atFormatted">{{ err.atFormatted }}</span>
							<span class="sm-badge" [ngClass]="err.isRetriable ? 'sm-badge-ok' : 'sm-badge-warn'">
								{{ (err.isRetriable ? 'administration.sync-monitoring.errors.retriable' : 'administration.sync-monitoring.errors.non-retriable') | translate }}
							</span>
							<strong>{{ err.tableName }}</strong>
							<span>{{ err.failure }}</span>
							<span class="sm-muted">
								({{ err.failureKind }}
								<ng-container *ngIf="err.failureScope">, {{ err.failureScope }}</ng-container>
								)
							</span>
						</div>
					</ng-container>
				</div>

				<!-- Transitions view (R17) -->
				<div class="sync-monitoring-section sync-monitoring-section--status" *ngIf="activeView === 'transitions'">
					<div class="sync-monitoring-loading" *ngIf="isTransitionsLoading">
						<mat-progress-spinner diameter="28" mode="indeterminate"></mat-progress-spinner>
					</div>
					<div class="sync-monitoring-error" *ngIf="transitionsError && !isTransitionsLoading">
						{{ transitionsError | translate }}
					</div>
					<ng-container *ngIf="!transitionsError && !isTransitionsLoading">
						<div class="sync-monitoring-empty" *ngIf="transitions.length === 0">
							{{ 'administration.sync-monitoring.transitions.empty' | translate }}
						</div>
						<div class="sm-transition-row" *ngFor="let transition of transitions">
							<span class="sm-transition-time">
								{{ transition.lastSeenFormatted }}
							</span>
							<span class="sm-transition-body">
								{{ transition.source }} / {{ transition.event }} / {{ transition.action }} → {{ transition.outcome }}
								<span class="sm-muted">({{ transition.previousState }} → {{ transition.nextState }})</span>
								<span class="sm-badge" *ngIf="transition.count > 1">×{{ transition.count }}</span>
							</span>
							<span class="sm-transition-context" *ngIf="transition.context">
								{{ transition.context }}
							</span>
						</div>
					</ng-container>
				</div>
			</mat-tab-nav-panel>
		</mat-card>
	</ng-container>
</div>
